import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Filter } from "lucide-react";

const CATEGORIES = ["todas", "limpieza", "tiempos", "trato", "precios", "seguridad", "stock", "otros"];
const STATUSES = [
  { v: "", l: "Todos" },
  { v: "pendiente", l: "Pendiente" },
  { v: "revisado", l: "Revisado" },
];

export default function AdminSugerencias() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("todas");
  const [statusF, setStatusF] = useState("");
  const [onlyUrgent, setOnlyUrgent] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (cat !== "todas") params.category = cat;
    if (statusF) params.status_f = statusF;
    if (onlyUrgent) params.urgent = true;
    try {
      const { data } = await api.get("/suggestions", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [cat, statusF, onlyUrgent]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try {
      await api.put(`/suggestions/${id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-sugerencias">
      <header>
        <h1 className="font-display font-black text-3xl">Sugerencias e incidencias</h1>
        <p className="text-muted-foreground">Filtra, marca y gestiona.</p>
      </header>

      <div className="bg-white rounded-2xl border border-border p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1 text-sm text-muted-foreground"><Filter size={14} /> Filtros</div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-lg border border-border px-3 py-1.5 text-sm" data-testid="filter-category">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="rounded-lg border border-border px-3 py-1.5 text-sm" data-testid="filter-status">
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyUrgent} onChange={(e) => setOnlyUrgent(e.target.checked)} data-testid="filter-urgent" /> Solo urgentes
        </label>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-8 text-center text-muted-foreground" data-testid="sugerencias-empty">
          Sin resultados con estos filtros.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => (
            <li key={s.id} className={`bg-white rounded-2xl border p-5 ${s.urgent ? "border-destructive/50" : "border-border"}`} data-testid={`sugerencia-${s.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${s.type === "incidencia" ? "bg-destructive text-destructive-foreground" : "bg-secondary text-secondary-foreground"}`}>
                  {s.type}
                </span>
                <span className="text-[11px] uppercase tracking-widest bg-accent px-2 py-0.5 rounded-full">{s.category}</span>
                <span className="text-[11px] uppercase tracking-widest bg-accent px-2 py-0.5 rounded-full">{s.franja}</span>
                <span className="text-[11px] uppercase tracking-widest bg-accent px-2 py-0.5 rounded-full">{s.origen}</span>
                <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${s.status === "revisado" ? "bg-emerald-600 text-white" : "bg-primary/10 text-primary"}`}>
                  {s.status}
                </span>
                {s.urgent && <span className="text-[11px] uppercase tracking-widest bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full font-bold">URGENTE</span>}
                <span className="ml-auto text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString("es-ES")}</span>
              </div>
              <p className="text-base whitespace-pre-line">{s.message}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => update(s.id, { status: s.status === "pendiente" ? "revisado" : "pendiente" })}
                  className="inline-flex items-center gap-1 bg-foreground text-background px-3 py-1.5 rounded-full text-sm"
                  data-testid={`sugerencia-toggle-status-${s.id}`}
                >
                  <CheckCircle2 size={14} /> {s.status === "pendiente" ? "Marcar revisado" : "Volver a pendiente"}
                </button>
                <button
                  onClick={() => update(s.id, { urgent: !s.urgent })}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm ${s.urgent ? "bg-accent" : "bg-destructive text-destructive-foreground"}`}
                  data-testid={`sugerencia-toggle-urgent-${s.id}`}
                >
                  <AlertTriangle size={14} /> {s.urgent ? "Quitar urgente" : "Marcar urgente"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
